import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAllCompanySubscriptions, extendTrialPeriod, endTrialPeriod, cancelSubscription, previewInvoice } from '../../api/subscription';
import { toast } from 'react-toastify';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Loader from '../../component/Loader';
import BackButton from '../../component/BackButton';
import SubscriptionPlansModal from '../../component/SubscriptionPlansModal';
import moment from 'moment';

function CompanySubscriptionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [expandedSection, setExpandedSection] = useState(null);
  const [extendingTrial, setExtendingTrial] = useState(null);
  const [endingTrial, setEndingTrial] = useState(false);
  const [trialExtensionDays, setTrialExtensionDays] = useState(0);
  const [minExtensionDays, setMinExtensionDays] = useState(1);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionModalMode, setSubscriptionModalMode] = useState('plans');
  const [invoicePreviews, setInvoicePreviews] = useState({});

  useEffect(() => {
    fetchSubscriptionDetails();
  }, [id]);

  useEffect(() => {
    setInvoicePreviews({});
  }, [subscriptions]);

  useEffect(() => {
    if (!expandedSection || subscriptions.length === 0) return;
    const index = parseInt(expandedSection.replace('subscription', ''));
    if (isNaN(index) || index < 0 || index >= subscriptions.length) return;
    const subscription = subscriptions[index];
    if (isSubscriptionCancelled(subscription)) return;
    if (!subscription.stripe_customer_id || !subscription.stripe_subscription_id) return;
    if (invoicePreviews[subscription.id]) return;
    let cancelled = false;
    const fetchPreview = async () => {
      try {
        const res = await previewInvoice(subscription.stripe_customer_id, subscription.stripe_subscription_id);
        if (!cancelled && res?.code === 1) {
          setInvoicePreviews(prev => ({ ...prev, [subscription.id]: res.data }));
        }
      } catch (err) {
        console.error('Error fetching invoice preview:', err);
      }
    };
    fetchPreview();
    return () => { cancelled = true; };
  }, [expandedSection, subscriptions, invoicePreviews]);

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const response = await getAllCompanySubscriptions(id);
      
      if (response?.code === 1 && response?.data) {
        setCompanyData(response.data.company);
        setSubscriptions(response.data.subscriptions || []);
        
        // Calculate remaining trial days and set as default/minimum
        const daysLeft = calculateTrialDaysLeft(response.data.company.trial_end);
        if (daysLeft !== null && daysLeft > 0) {
          setTrialExtensionDays(daysLeft);
          setMinExtensionDays(daysLeft);
        } else {
          setTrialExtensionDays(7);
          setMinExtensionDays(1);
        }
      } else {
        setCompanyData(null);
        setSubscriptions([]);
        toast.info('No company found or no subscriptions available');
      }
    } catch (error) {
      console.error('Error fetching subscription details:', error);
      toast.error('Error fetching subscription details');
      setCompanyData(null);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const calculateTrialDaysLeft = (trialEndDate) => {
    if (!trialEndDate) return null;
    const now = moment();
    const trialEnd = moment(trialEndDate);
    const daysLeft = trialEnd.diff(now, 'days');
    return daysLeft >= 0 ? daysLeft : 0;
  };

  const handleEndTrial = async () => {
    if (!window.confirm('Are you sure you want to end the trial immediately?')) return;
    try {
      setEndingTrial(true);
      const response = await endTrialPeriod({ company_id: id });
      if (response?.code === 1) {
        toast.success('Trial ended successfully');
        await fetchSubscriptionDetails();
      } else {
        toast.error(response?.message || 'Failed to end trial');
      }
    } catch (error) {
      console.error('Error ending trial:', error);
      toast.error('Error ending trial period');
    } finally {
      setEndingTrial(false);
    }
  };

  const handleExtendTrial = async () => {
    try {
      setExtendingTrial('company');
      
      // Call API to extend trial using company_id
      const response = await extendTrialPeriod({
        company_id: id,
        extension_days: trialExtensionDays
      });
      
      if (response?.code === 1) {
        toast.success(`Trial extended by ${trialExtensionDays} days successfully`);
        // Refresh subscription data
        await fetchSubscriptionDetails();
      } else {
        toast.error(response?.message || 'Failed to extend trial');
      }
    } catch (error) {
      console.error('Error extending trial:', error);
      toast.error('Error extending trial period');
    } finally {
      setExtendingTrial(null);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?\n\nThe subscription will be cancelled at the end of the current billing period, and the company can continue to use all features until then.\n\nClick OK to proceed or Cancel to keep the subscription active.')) {
      return;
    }

    try {
      const response = await cancelSubscription({
        company_id: id,
        stripe_subscription_id: subscriptionId
      });

      if (response?.code === 1) {
        toast.success(response?.message || 'Subscription cancelled successfully');
        await fetchSubscriptionDetails();
      } else {
        toast.error(response?.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Error cancelling subscription');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { color: 'success', icon: 'fa-check-circle' },
      'trialing': { color: 'warning', icon: 'fa-clock' },
      'cancelled': { color: 'danger', icon: 'fa-times-circle' },
      'canceled': { color: 'danger', icon: 'fa-times-circle' },
      'past_due': { color: 'danger', icon: 'fa-exclamation-triangle' },
      'incomplete': { color: 'secondary', icon: 'fa-minus-circle' }
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig['incomplete'];
    return (
      <span className={`badge bg-${config.color} me-2`}>
        <i className={`fa ${config.icon} me-1`}></i>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
      </span>
    );
  };

  const isSubscriptionCancelled = (sub) => {
    return ['cancelled', 'canceled'].includes(sub?.status?.toLowerCase()) || !!sub?.cancelled_at;
  };

  if (loading) {
    return (
      <article className="col-12 col-lg-9 articleClass">
        <Loader />
      </article>
    );
  }

  return (
    <article className="col-12 col-lg-9 articleClass">
      <div className="article-body store-bd-gy">
        <div className="row align-items-center col-rever">
          <div className="col-12">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center mb-2 mb-md-0">
                <BackButton />  
                {companyData && (
                      <span className="ms-2 ms-md-3" style={{color: '#2c59ac'}}>
                        {companyData.company_name}
                      </span>
                )} 
              </div>
              <div className="text-center text-md-end">
                <h3 className="mb-0">
                  Subscription Details
                </h3>
              </div>
            </div>
          </div>
        </div>

        {/* Trial Details Section */}
        {companyData && (
          <div className="card mb-4" style={{borderColor: '#2c59ac'}}>
            <div className="card-body">
              <h5 className="mb-3" style={{color: '#2c59ac'}}>
                <span className="fa fa-clock me-2"></span>
                Trial Details
              </h5>
              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label text-muted">Trial Start Date</label>
                    <p className="form-control-plaintext">
                      {companyData.trial_start 
                        ? moment(companyData.trial_start).format('MMMM DD, YYYY HH:mm')
                        : 'Not set'
                      }
                    </p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label text-muted">Trial End Date</label>
                    <p className="form-control-plaintext">
                      {companyData.trial_end 
                        ? moment(companyData.trial_end).format('MMMM DD, YYYY HH:mm')
                        : 'Not set'
                      }
                    </p>
                  </div>
                </div>
                 <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label text-muted">Trial Status</label>
                    <div>
                      {companyData.trial_end ? (
                        calculateTrialDaysLeft(companyData.trial_end) > 0 ? (
                          <span className="badge bg-success">
                            <i className="fa fa-check-circle me-1"></i>
                            Active - {calculateTrialDaysLeft(companyData.trial_end)} days remaining
                          </span>
                        ) : (
                          <span className="badge bg-danger">
                            <i className="fa fa-times-circle me-1"></i>
                            Trial Expired
                          </span>
                        )
                      ) : (
                        <span className="badge bg-secondary">
                          <i className="fa fa-minus-circle me-1"></i>
                          No Trial
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {companyData.trial_end && calculateTrialDaysLeft(companyData.trial_end) > 0 && (
                <div className="row mt-3 pt-3 border-top">
                  <div className="col-12">
                    <label className="form-label text-muted mb-2">Extend Trial Period</label>
                    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                      <div className="d-flex align-items-center gap-2 flex-grow-1">
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Days"
                          style={{maxWidth: '120px'}}
                          value={trialExtensionDays}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 0;
                            setTrialExtensionDays(value);
                          }}
                        />
                        <span className="text-muted small">days</span>
                      </div>
                      <button
                        className="btn btn-sm flex-shrink-0"
                        style={{backgroundColor: '#2c59ac', borderColor: '#2c59ac', color: 'white'}}
                        onClick={handleExtendTrial}
                        disabled={extendingTrial === 'company'}
                      >
                        {extendingTrial === 'company' ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1"></span>
                            Extending...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-plus-circle me-1"></i>
                            Extend Trial
                          </>
                        )}
                      </button>
                      <button
                      className="btn btn-sm btn-danger flex-shrink-0"
                      onClick={handleEndTrial}
                      disabled={endingTrial}
                    >
                      {endingTrial ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1"></span>
                          Ending...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-stop-circle me-1"></i>
                          End Trial Now
                        </>
                      )}
                    </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {subscriptions.length === 0 ? (
          <div className="text-center py-5">
            <span className="fa fa-crown fa-4x text-muted mb-3"></span>
            <h4 className="text-muted">No Subscriptions Found</h4>
            <p className="text-muted">This company doesn't have any subscriptions.</p>
          </div>
        ) : (
          <div className="row mt-4">
            <div className="col-12">
              {/* Multiple Subscriptions Accordion */}
              <div className="accordion" id="subscriptionAccordion">
                {subscriptions.map((subscription, index) => (
                  <div key={subscription.id} className="accordion-item mb-3">
                    <h2 className="accordion-header" id={`subscriptionHeader${index}`}>
                      <button
                        className={`accordion-button ${expandedSection === `subscription${index}` ? '' : 'collapsed'}`}
                        type="button"
                        onClick={() => toggleSection(`subscription${index}`)}
                      >
                        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between w-100 gap-2">
                          <div className="d-flex align-items-center">
                            <span className="fa fa-crown me-2" style={{color: '#2c59ac'}}></span>
                            <strong className="me-2">{subscription.plan?.plan_name || 'Unknown Plan'}</strong>
                          </div>
                          <div className="d-flex flex-column flex-md-row align-items-center gap-2 text-md-end">
                            <span className="text-muted small">
                              {moment(subscription.createdAt).format('MMM DD, YYYY')}
                            </span>
                            {getStatusBadge(subscription.status)}
                          </div>
                        </div>
                      </button>
                    </h2>
                    <div
                      className={`accordion-collapse collapse ${expandedSection === `subscription${index}` ? 'show' : ''}`}
                      id={`subscriptionCollapse${index}`}
                    >
                      <div className="accordion-body">
                        {/* Subscription Basic Information */}
                        <div className="row mb-4">
                          <div className="col-12">
                            <h5 className="mb-3" style={{color: '#2c59ac'}}>
                              <span className="fa fa-info-circle me-2"></span>
                              Subscription Information
                            </h5>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Subscription ID</label>
                              <p className="form-control-plaintext">{subscription.stripe_subscription_id || 'N/A'}</p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Customer ID</label>
                              <p className="form-control-plaintext">{subscription.stripe_customer_id || 'N/A'}</p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Status</label>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                {getStatusBadge(subscription.status)}
                                {subscription.status === 'trialing' && subscription.trial_end && (
                                  <span className="badge bg-info flex-shrink-0">
                                    <i className="fa fa-clock me-1"></i>
                                    {calculateTrialDaysLeft(subscription.trial_end)} days left
                                  </span>
                                )}
                                {!isSubscriptionCancelled(subscription) && subscription.stripe_subscription_id && (
                                  <button
                                    className="btn btn-sm btn-outline-danger flex-shrink-0"
                                    onClick={() => handleCancelSubscription(subscription.stripe_subscription_id)}
                                  >
                                    <i className="fa fa-times-circle me-1"></i>
                                    Cancel Subscription
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Started Date</label>
                              <p className="form-control-plaintext">
                                {subscription.current_period_start 
                                  ? moment(subscription.current_period_start).format('MMMM DD, YYYY')
                                  : 'N/A'
                                }
                              </p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">{isSubscriptionCancelled(subscription) ? 'Cancellation Date' : 'Next Billing Date'}</label>
                              <p className="form-control-plaintext">
                                {isSubscriptionCancelled(subscription)
                                  ? `This subscription will be cancelled at ${moment(subscription.cancelled_at || subscription.current_period_end).format('MMMM DD, YYYY')}`
                                  : (subscription.current_period_end 
                                    ? `${moment(subscription.current_period_end).format('MMMM DD, YYYY')}${subscription.pending_plan_id ? ` (${subscription.pendingPlan?.plan_name || 'Unknown Plan'})` : ''}`
                                    : 'N/A')
                                }
                              </p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Trial Information</label>
                              {subscription.trial_end ? (
                                <div>
                                  <p className="form-control-plaintext mb-2">
                                    <strong>End Date:</strong> {moment(subscription.trial_end).format('MMMM DD, YYYY')}
                                  </p>
                                  {subscription.status === 'trialing' && calculateTrialDaysLeft(subscription.trial_end) > 0 && (
                                    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                                     
                                      <div className="d-flex align-items-center gap-2 flex-grow-1">
                                        <input
                                          type="number"
                                          className="form-control form-control-sm"
                                          placeholder="Days"
                                          min="1"
                                          max="90"
                                          value={trialExtensionDays}
                                          onChange={(e) => setTrialExtensionDays(Math.min(90, Math.max(1, parseInt(e.target.value) || 1)))}
                                        />
                                      </div>
                                      <button
                                        className="btn btn-sm flex-shrink-0"
                                        style={{backgroundColor: '#2c59ac', borderColor: '#2c59ac', color: 'white'}}
                                        onClick={() => handleExtendTrial(subscription.id)}
                                        disabled={extendingTrial === subscription.id}
                                      >
                                        {extendingTrial === subscription.id ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                            Extending...
                                          </>
                                        ) : (
                                          <>
                                            Extend Trial Days
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="form-control-plaintext">No trial period</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Plan Details */}
                        <div className="row mb-4">
                          <div className="col-12">
                            <h5 className="mb-3" style={{color: '#2c59ac'}}>
                              <span className="fa fa-crown me-2"></span>
                              Plan Details
                            </h5>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Plan Name</label>
                              <p className="form-control-plaintext">{subscription.plan?.plan_name || 'N/A'}</p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Description</label>
                              <p className="form-control-plaintext">{subscription.plan?.description || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Price</label>
                              <p className="form-control-plaintext">
                                £{subscription.plan?.price || '0'} / {subscription.plan?.billing_interval || 'month'}
                              </p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Student Limit</label>
                              <p className="form-control-plaintext">
                                {subscription.plan?.student_limit || 'Unlimited'} students
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Plan Summary */}
                        <div className="row mb-4">
                          <div className="col-12">
                            <div className="card" style={{ backgroundColor: '#f8faff', borderColor: '#d4e1f5', borderRadius: '10px', borderWidth: '1px' }}>
                              <div className="card-body p-4">
                                <h6 className="card-title mb-3" style={{ color: '#2c59ac', fontSize: '0.95rem', fontWeight: 600 }}>
                                  <i className="fa fa-exchange me-2"></i>
                                  Plan Summary
                                </h6>
                                <div className="row">
                                  <div className="col-md-6">
                                    <div className="mb-2">
                                      <label className="form-label text-muted mb-1" style={{ fontSize: '0.8rem' }}>Current Plan</label>
                                      <p className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem' }}>{subscription.plan?.plan_name || 'N/A'}</p>
                                    </div>
                                    <div className="mb-2">
                                      <label className="form-label text-muted mb-1" style={{ fontSize: '0.8rem' }}>Next / Scheduled Plan</label>
                                      <p className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem' }}>
                                        {isSubscriptionCancelled(subscription)
                                          ? 'N/A (subscription cancelled)'
                                          : subscription.pending_plan_id
                                            ? (subscription.pendingPlan?.plan_name || 'Unknown Plan')
                                            : (subscription.plan?.plan_name || 'N/A')}
                                      </p>
                                    </div>
                                    <div className="mb-2 mb-md-0">
                                      <label className="form-label text-muted mb-1" style={{ fontSize: '0.8rem' }}>Request Date</label>
                                      <p className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem' }}>
                                        {subscription.updatedAt ? moment(subscription.updatedAt).format('MMMM DD, YYYY') : 'N/A'}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    {(() => {
                                      const effectiveDate = isSubscriptionCancelled(subscription)
                                        ? subscription.cancelled_at
                                        : (subscription.pending_plan_id ? subscription.pending_plan_effective_date : null);
                                      if (!effectiveDate) return null;
                                      return (
                                        <div className="mb-2">
                                          <label className="form-label text-muted mb-1" style={{ fontSize: '0.8rem' }}>Effective Date</label>
                                          <p className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem' }}>
                                            {moment(effectiveDate).format('MMMM DD, YYYY')}
                                          </p>
                                        </div>
                                      );
                                    })()}
                                    <div className="mb-0">
                                      <label className="form-label text-muted mb-1" style={{ fontSize: '0.8rem' }}>Next Billing Date</label>
                                      <p className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem' }}>
                                        {isSubscriptionCancelled(subscription) ? 'N/A' : (subscription.current_period_end ? moment(subscription.current_period_end).format('MMMM DD, YYYY') : 'N/A')}
                                      </p>
                                    </div>
                                    <div className="mb-0">
                                      <label className="form-label text-muted mb-1" style={{ fontSize: '0.8rem' }}>Next Billing Amount</label>
                                      <p className="form-control-plaintext mb-0" style={{ fontSize: '0.85rem' }}>
                                        {isSubscriptionCancelled(subscription)
                                          ? 'N/A'
                                          : (() => {
                                              const nextInvoice = invoicePreviews[subscription.id];
                                              const basePlan = subscription.pending_plan_id ? subscription.pendingPlan : subscription.plan;
                                              const allAddons = subscription.addons || [];
                                              const paidAddons = allAddons.filter(a => a.stripe_subscription_item_id && !a.scheduled_for_removal);
                                              const basePrice = parseFloat(basePlan?.price || 0);
                                              const addonsTotal = paidAddons.reduce((sum, a) => sum + parseFloat(a.addon?.price || 0), 0);
                                              const computedTotal = basePrice + addonsTotal;
                                              const currencySymbol = nextInvoice?.currency?.toLowerCase() === 'usd' ? '$' : '£';
                                              const displayAmount = nextInvoice ? parseFloat(nextInvoice.amount) : computedTotal;
                                              const breakdownTooltip = (
                                                <Tooltip id={`next-billing-breakdown-tooltip-${subscription.id}`} style={{
                                                  '--bs-tooltip-max-width': '320px',
                                                  '--bs-tooltip-font-size': '0.9rem',
                                                  '--bs-tooltip-padding-x': '12px',
                                                  '--bs-tooltip-padding-y': '12px',
                                                  '--bs-tooltip-bg': '#fff',
                                                  '--bs-tooltip-color': '#212529',
                                                  backgroundColor: '#fff',
                                                  color: '#212529',
                                                  border: '1px solid #dee2e6',
                                                  borderRadius: '8px'
                                                }}>
                                                  <div className="text-start" style={{ minWidth: '180px' }}>
                                                    {nextInvoice?.lines?.length > 0 ? (
                                                      nextInvoice.lines.map((line, idx) => (
                                                        <div key={idx} className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                                                          <span>{line.description || `Line ${idx + 1}`}</span>
                                                          <span>{currencySymbol}{parseFloat(line.amount || 0).toFixed(2)}</span>
                                                        </div>
                                                      ))
                                                    ) : (
                                                      <>
                                                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                                                          <span>{basePlan?.plan_name || 'Plan'}</span>
                                                          <span>{currencySymbol}{basePrice.toFixed(2)}</span>
                                                        </div>
                                                        {allAddons.map((a, idx) => {
                                                          const addonPrice = a.stripe_subscription_item_id ? parseFloat(a.addon?.price || 0) : 0;
                                                          return (
                                                            <div key={a.id || idx} className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                                                              <span>{a.addon?.addon_name || `Addon ${idx + 1}`}</span>
                                                              <span>{currencySymbol}{addonPrice.toFixed(2)}</span>
                                                            </div>
                                                          );
                                                        })}
                                                      </>
                                                    )}
                                                    <div className="d-flex justify-content-between border-top pt-1 mt-1" style={{ fontSize: '0.8rem', fontWeight: 700 }}>
                                                      <span>Total</span>
                                                      <span>{currencySymbol}{displayAmount.toFixed(2)}</span>
                                                    </div>
                                                  </div>
                                                </Tooltip>
                                              );
                                              return (
                                                <span>
                                                  {currencySymbol}{displayAmount.toFixed(2)}/month
                                                  {allAddons.length > 0 && (
                                                    <OverlayTrigger trigger="click" placement="right" rootClose overlay={breakdownTooltip}>
                                                      <a href="#" style={{ fontSize: '0.75rem', color: '#2c59ac', textDecoration: 'underline', fontWeight: 500, marginLeft: '8px' }} onClick={(e) => e.preventDefault()}>View breakdown</a>
                                                    </OverlayTrigger>
                                                  )}
                                                </span>
                                              );
                                            })()}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {subscription.pending_plan_id && subscription.pending_plan_effective_date && (
                          <div className="row mb-4">
                            <div className="col-12">
                              <div className="alert alert-info mb-0">
                                <i className="fa fa-info-circle me-2"></i>
                                Plan will change to <strong>{subscription.pendingPlan?.plan_name || 'Unknown Plan'}</strong> on <strong>{moment(subscription.pending_plan_effective_date).format('MMMM DD, YYYY')}</strong>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Add-ons Section */}
                        <div className="row mb-4">
                          <div className="col-12">
                            <h5 className="mb-3" style={{color: '#2c59ac'}}>
                              <span className="fa fa-puzzle-piece me-2"></span>
                              Add-ons ({subscription.addons?.length || 0})
                            </h5>
                          </div>
                          {subscription.addons && subscription.addons.length > 0 ? (
                            <div className="col-12">
                              <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-3">
                                {subscription.addons.map((addon, addonIndex) => (
                                  <div key={addonIndex} className="col">
                                    <div className="card h-100" style={{borderColor: '#2c59ac'}}>
                                      <div className="card-body">
                                        <h6 className="card-title">
                                          <span className="fa fa-puzzle-piece me-2" style={{color: '#2c59ac'}}></span>
                                          {addon.addon?.addon_name || 'Unknown Addon'}
                                        </h6>
                                        {addon.scheduled_for_removal && (
                                          <div className="alert alert-warning py-1 px-2 mb-2" style={{fontSize: '0.8rem'}}>
                                            <i className="fa fa-exclamation-triangle me-1"></i>
                                            Cancels on {addon.removal_effective_date ? moment(addon.removal_effective_date).format('MMMM DD, YYYY') : 'end of billing period'}
                                          </div>
                                        )}
                                        <div className="card-text">
                                          <p className="mb-2">
                                            <strong>Price:</strong> £{addon.addon?.price || '0'}
                                          </p>
                                          <p className="mb-2">
                                            <strong>Description:</strong> {addon.addon?.description || 'No description'}
                                          </p>
                                          <p className="mb-0">
                                            <strong>Stripe Item ID:</strong> 
                                            <span className="d-block text-break small">
                                              {addon.stripe_subscription_item_id || 'N/A'}
                                            </span>
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="col-12">
                              <div className="text-center py-3 bg-light rounded">
                                <span className="fa fa-puzzle-piece fa-2x text-muted mb-2"></span>
                                <p className="text-muted mb-0">No add-ons active for this subscription</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Billing Information */}
                        <div className="row">
                          <div className="col-12">
                            <h5 className="mb-3" style={{color: '#2c59ac'}}>
                              <span className="fa fa-credit-card me-2"></span>
                              Billing Information
                            </h5>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Current Period Start</label>
                              <p className="form-control-plaintext">
                                {subscription.current_period_start 
                                  ? moment(subscription.current_period_start).format('MMMM DD, YYYY HH:mm')
                                  : 'N/A'
                                }
                              </p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">Current Period End</label>
                              <p className="form-control-plaintext">
                                {subscription.current_period_end 
                                  ? moment(subscription.current_period_end).format('MMMM DD, YYYY HH:mm')
                                  : 'N/A'
                                }
                              </p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label text-muted">Trial End</label>
                              <p className="form-control-plaintext">
                                {subscription.trial_end 
                                  ? moment(subscription.trial_end).format('MMMM DD, YYYY HH:mm')
                                  : 'No trial period'
                                }
                              </p>
                            </div>
                            <div className="mb-3">
                              <label className="form-label text-muted">{isSubscriptionCancelled(subscription) ? 'Days Until Cancellation' : 'Days Until Next Billing'}</label>
                              <p className="form-control-plaintext">
                                {subscription.current_period_end 
                                  ? Math.max(0, moment(subscription.current_period_end).diff(moment(), 'days')) + ' days'
                                  : 'N/A'
                                }
                              </p>
                            </div>
                          </div>
                          {subscription.payments && subscription.payments.length > 0 && (
                            <div className="col-12 mt-2">
                              <label className="form-label text-muted mb-2 d-block">Invoices</label>
                              <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                  <tbody>
                                    {subscription.payments.map((payment, idx) => {
                                      const invoiceName = payment.stripe_invoice_id
                                        ? `Invoice ${payment.stripe_invoice_id}`
                                        : `Payment #${payment.id ?? '—'}`;
                                      const created = payment.created_at || payment.paid_at;
                                      return (
                                        <tr key={payment.id ?? payment.stripe_invoice_id ?? `payment-row-${idx}`}>
                                          <td>{invoiceName}</td>
                                          <td>
                                            {created
                                              ? moment(created).format('MMMM DD, YYYY HH:mm')
                                              : '—'}
                                          </td>
                                          <td className="text-end">
                                            {payment.invoice_pdf ? (
                                              <a
                                                href={payment.invoice_pdf}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-decoration-none"
                                              >
                                                <span className="fa fa-file-pdf-o me-1" aria-hidden="true"></span>
                                                View
                                              </a>
                                            ) : (
                                              <span className="text-muted small">—</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="row mt-4">
                          <div className="col-12">
                            <div className="d-flex flex-wrap gap-2">
                              <button
                                className="btn btn-sm"
                                style={{ backgroundColor: '#2c59ac', borderColor: '#2c59ac', color: 'white' }}
                                onClick={() => {
                                  setSelectedSubscription(subscription);
                                  setSubscriptionModalMode('plans');
                                  setShowSubscriptionModal(true);
                                }}
                              >
                                <i className="fa fa-exchange me-1"></i>
                                Upgrade / Downgrade Plan
                              </button>
                              {(['active', 'trialing'].includes(subscription.status?.toLowerCase()) && !isSubscriptionCancelled(subscription)) && (
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => {
                                    setSelectedSubscription(subscription);
                                    setSubscriptionModalMode('details');
                                    setShowSubscriptionModal(true);
                                  }}
                                >
                                  <i className="fa fa-puzzle-piece me-1"></i>
                                  Manage Add-ons
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <SubscriptionPlansModal
        show={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          fetchSubscriptionDetails();
        }}
        companySubscription={selectedSubscription}
        companyId={Number(id) || id}
        initialViewMode={subscriptionModalMode}
      />
    </article>
  );
}

export default CompanySubscriptionDetails;
